/**
 * @lodex-pro/web-admin/features/site-types/hooks — 86bc7raat.
 *
 * TanStack Query hooks over /v1/platform/site-types (MANAGE_SITE_TYPES, PLATFORM_ADMIN).
 * GET returns every row including inactive ones, which is why it is a platform-admin
 * read rather than the broadly-granted tenant read at /v1/site-types.
 *
 * There is no delete hook, deliberately: a site type is deactivated via
 * PATCH { isActive: false } and never removed, because tenant_site_type_settings and
 * sites reference it. Migration 0089 grants INSERT + UPDATE only, so the API could not
 * hard-delete even if asked.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';

export interface SiteType {
  readonly id: string;
  /** Stable machine identifier; settable at create, immutable afterwards. */
  readonly code: string;
  readonly label: string;
  readonly sortOrder: number;
  readonly isActive: boolean;
}

export const SITE_TYPES_KEY = ['platform', 'site-types'] as const;

export function useSiteTypes(): ReturnType<typeof useApiQuery<{ siteTypes: readonly SiteType[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ siteTypes: readonly SiteType[] }>(opts, '/v1/platform/site-types', {
    queryKey: SITE_TYPES_KEY,
    staleTime: 15_000,
  });
}

export interface CreateSiteTypeBody {
  code: string;
  label: string;
  sortOrder?: number;
}

export function useCreateSiteType(
  onDone: () => void,
): ReturnType<typeof useApiMutation<CreateSiteTypeBody, { siteType: SiteType }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<CreateSiteTypeBody, { siteType: SiteType }>(
    opts,
    '/v1/platform/site-types',
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: SITE_TYPES_KEY });
        onDone();
      },
    },
  );
}

export interface UpdateSiteTypeBody {
  label?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export function useUpdateSiteType(
  id: string,
): ReturnType<typeof useApiMutation<UpdateSiteTypeBody, { siteType: SiteType }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<UpdateSiteTypeBody, { siteType: SiteType }>(
    opts,
    `/v1/platform/site-types/${id}`,
    'PATCH',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: SITE_TYPES_KEY }) },
  );
}
